import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  TextField,
  Divider,
} from '@mui/material';
import { AppContext } from '../../../../context/AppContextProvider';
import ButtonComponent from '../../../../components/ButtonComponent';
import { useGetConfigByIdAsync, useUpdateConfigAsync } from '../../../../services/Configs/useMutation';
import { getLicenseId } from '../../../../services/DefaultService';

const GeneralManagementTab = () => {
  const { showSnackbar } = useContext(AppContext);
  const licenseId = getLicenseId() ?? '';

  // Estados locais sincronizados com o seu Back-end em C#
  const [appDistanceRadiusMeters, setAppDistanceRadiusMeters] = useState<number>(200);
  const [screenEventRefreshIntervalMin, setScreenEventRefreshIntervalMin] = useState<number>(5);
  const [showOnlyActiveStudentsResponsibles, setShowOnlyActiveStudentsResponsibles] = useState<boolean>(true);
  const [notificationTimeSameFace, setNotificationTimeSameFace] = useState<number>(0);

  // NOVOS ESTADOS ADICIONADOS
  const [code, setCode] = useState<string>('');
  const [urlApp, setUrlApp] = useState<string>('');
  const [backGroundApp, setBackGroundApp] = useState<string>('');
  const [icon, setIcon] = useState<string>('');

  // Hooks do react-query
  const { mutateAsync: getConfigs } = useGetConfigByIdAsync();
  const { mutateAsync: updateConfigs, isLoading } = useUpdateConfigAsync();

  // Busca as configurações do Firestore ao carregar a tela
  useEffect(() => {
    getConfigs()
      .then((data) => {
        if (data) {
          setAppDistanceRadiusMeters(data.appDistanceRadiusMeters ?? 200);
          setScreenEventRefreshIntervalMin(data.screenEventRefreshIntervalMin ?? 5);
          setShowOnlyActiveStudentsResponsibles(data.showOnlyActiveStudentsResponsibles ?? true);
          setNotificationTimeSameFace(data.notificationTimeSameFace ?? 0);

          // Mapeamento dos novos campos retornados pelo DTO
          setCode(data.code ?? '');
          setUrlApp(data.urlApp ?? '');
          setBackGroundApp(data.backGroundApp ?? '');
          setIcon(data.icon ?? '');
        }
      })
      .catch(() => {
        showSnackbar('Erro ao carregar configurações gerais.', 'error');
      });
  }, []);

  const handleSave = () => {
    // Validações de segurança antes de disparar a requisição
    if (appDistanceRadiusMeters <= 0) {
      showSnackbar('O raio de distância deve ser maior que zero.', 'error');
      return;
    }

    if (screenEventRefreshIntervalMin < 1) {
      showSnackbar('O tempo de atualização da tela deve ser de pelo menos 1 minuto.', 'error');
      return;
    }

    if (notificationTimeSameFace < 0) {
      showSnackbar('O tempo de notificação para a mesma face não pode ser negativo.', 'error');
      return;
    }

    // Envia o payload completo estruturado igual ao LicenseConfigUpdateDto do C#
    updateConfigs({
      data: {
        appDistanceRadiusMeters,
        screenEventRefreshIntervalMin,
        showOnlyActiveStudentsResponsibles,
        notificationTimeSameFace,
        code,
        urlApp,
        backGroundApp,
        icon
      },
    })
      .then(() => {
        showSnackbar('Configurações gerais salvas com sucesso.', 'success');
      })
      .catch((error: any) => {
        showSnackbar(error?.response?.data?.Message || 'Falha ao salvar configurações gerais.', 'error');
      });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configurações Gerais do Aplicativo
      </Typography>

      <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
        Gerencie os parâmetros de funcionamento do aplicativo, identidade visual, regras de exibição em painéis (TV/Tablet) e limites geográficos.
      </Typography>

      <Grid container spacing={3} sx={{ maxWidth: 600 }}>

        {/* SEÇÃO: Identidade e Conexão do Aplicativo (Novos campos adicionados aqui) */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Identidade & Conexão do App
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="License ID"
                fullWidth
                value={licenseId}
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Código do App"
                fullWidth
                value={code}
                onChange={(e) => setCode(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="URL / IP Externo do App"
                fullWidth
                value={urlApp}
                onChange={(e) => setUrlApp(e.target.value)}
                size="small"
                placeholder="Ex: 192.168.1.100"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="URL do Ícone (Imagem)"
                fullWidth
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="URL do Background (Imagem de Fundo)"
                fullWidth
                value={backGroundApp}
                onChange={(e) => setBackGroundApp(e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Seção de Geolocalização */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Parâmetros de Cerca Geográfica
          </Typography>
          <TextField
            label="Raio de Distância do Aplicativo (metros)"
            type="number"
            fullWidth
            value={appDistanceRadiusMeters}
            onChange={(e) => setAppDistanceRadiusMeters(Number(e.target.value))}
            inputProps={{ min: 1 }}
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Seção de Monitoramento de Telas */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Sincronização de Telas (TV / Tablet)
          </Typography>
          <TextField
            label="Tempo de Amostragem de Eventos (minutos)"
            type="number"
            fullWidth
            value={screenEventRefreshIntervalMin}
            onChange={(e) => setScreenEventRefreshIntervalMin(Number(e.target.value))}
            inputProps={{ min: 1 }}
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Tempo de Notificação para a Mesma Face (Minutos)"
            type="number"
            fullWidth
            value={notificationTimeSameFace}
            onChange={(e) => setNotificationTimeSameFace(Number(e.target.value))}
            inputProps={{ min: 0 }}
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Seção de Regras de Filtros */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Filtros Visuais
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyActiveStudentsResponsibles}
                onChange={() => setShowOnlyActiveStudentsResponsibles((prev) => !prev)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Mostrar apenas responsáveis com filhos na instituição
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  Quando ativo, a tela omitirá eventos de responsáveis cujos filhos já realizaram a saída.
                </Typography>
              </Box>
            }
          />
        </Grid>

        {/* Botão de Ação */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <ButtonComponent onClick={handleSave} loading={isLoading}>
            Salvar Configurações
          </ButtonComponent>
        </Grid>

      </Grid>
    </Box>
  );
};

export default GeneralManagementTab;
